import React, { ReactElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import { useDeviceIdInput } from '@controlant/utility-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@mui/material';

import { TOAST_TYPE_MAP, ToastCustom } from '../components/ToastCustom';
import { VerifyLabelModalData } from '../types/Modal';

import '../styles/modals/SharedModalStyles.less';
import '../styles/modals/VerifyLoggerLabelModal.less';

interface Props {
    modalData: VerifyLabelModalData | null;
}

const VerifyLoggerLabelModal = ({ modalData }: Props): ReactElement | null => {
    const verifyLoggerInputRef = useRef<HTMLInputElement>(null);
    const [notMatchingId, setNotMatchingId] = useState<string | null>(null);

    const { handleInputChange } = useDeviceIdInput({
        onValidId: async (formattedId) => {
            if (formattedId === modalData?.logger.loggerId) {
                modalData?.action(modalData.logger, 'ADD');
                modalData.closeMe();
                setNotMatchingId(null);
            } else {
                setNotMatchingId(formattedId);
            }
        },
        onInvalidId: (reason) => ToastCustom(reason, TOAST_TYPE_MAP.ERROR)
    });

    useEffect(() => {
        if (modalData?.logger) {
            setNotMatchingId('');
        }
    }, [modalData]);

    useLayoutEffect(() => {
        verifyLoggerInputRef?.current?.focus();
    }, [notMatchingId]);

    const handleCancel = () => {
        modalData?.action(modalData.logger, 'REMOVE');
        modalData?.closeMe();
        setNotMatchingId(null);
    };

    return (
        <>
            {modalData && (
                <Modal
                    isOpen={true}
                    ariaHideApp={false}
                    className="modal verify-modal"
                    overlayClassName="modal-overlay"
                    onRequestClose={handleCancel}
                    shouldCloseOnOverlayClick={false}
                >
                    <div className="modal-header" data-testid="modal-header">
                        Verify logger labels for logger {modalData.logger.loggerId}
                        <FontAwesomeIcon
                            onClick={handleCancel}
                            className="close-icon"
                            icon={['far', 'times']}
                            id="dimgray"
                        />
                    </div>

                    <div>
                        {!notMatchingId ? (
                            <div className="verify-input-container" data-testid="verify-input-container">
                                <div className="label-input-pair" data-testid="label-input-pair">
                                    <label className="control-label" data-testid="control-label">
                                        Please scan the 2nd barcode label
                                    </label>
                                    <input
                                        data-testid="logger-input-field"
                                        className="medium"
                                        ref={verifyLoggerInputRef}
                                        placeholder="Please scan ID"
                                        onChange={(e) => handleInputChange(e.target.value)}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="not-matching-container" data-testid="not-matching-container">
                                <div className="error-text">
                                    Logger ID's did not match, {modalData.logger.loggerId} does not match{' '}
                                    {notMatchingId}.
                                </div>
                                <div>Please confirm that the labels do not match and handle according to WIN.</div>
                                <div>If this was a mistake, please scan the logger again.</div>
                                <div className="modal-footer" data-testid="modal-footer">
                                    <Button variant="outlined" data-testid="close-btn" onClick={handleCancel}>
                                        Close
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </Modal>
            )}
        </>
    );
};

export default VerifyLoggerLabelModal;
